/*================================ COMMONS ==============================*/
import { CSSProperties } from "react";
import {
  generatePrefixedActions,
  BLC_STATUS,
  OptionType,
  BLC_STATUS_TYPE,
} from "../../../../../src/react-lib/apps/HISV3/common/CommonInterface";
import { collection, doc, Firestore, getDoc, setDoc, Timestamp, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";

/*================================ APIS ==============================*/
import BroadcastPostListCreateView from "issara-sdk/apis/BroadcastPostListCreateView_apps_PRX";
import PublishedDiagRuleNodeList from "issara-sdk/apis/PublishedDiagRuleNodeList_apps_PRX";
import PublishedDiagRuleNodeDetail from "issara-sdk/apis/PublishedDiagRuleNodeDetail_apps_PRX";
import DivisionDetail from "issara-sdk/apis/DivisionDetail_core";
import DivisionList from "issara-sdk/apis/DivisionList_core";
import BroadcastPostActionView from "issara-sdk/apis/BroadcastPostActionView_apps_PRX";
import BroadcastPostCalculateCreditView from "issara-sdk/apis/BroadcastPostCalculateCreditView_apps_PRX";
import UserEmployeeDetailAPIView from "issara-sdk/apis/UserEmployeeDetailAPIView_users";
import FindPatientsByLeafNodeAPI from "issara-sdk/apis/FindPatientsByLeafNodeAPI_apps_PRX";
import BroadcastPostActionLogList from "issara-sdk/apis/BroadcastPostActionLogList_apps_PRX";
import BroadcastPostDetailView from "issara-sdk/apis/BroadcastPostDetailView_apps_PRX";
import BroadcastPostDuplicateView from "issara-sdk/apis/BroadcastPostDuplicateView_apps_PRX";
import PublishedDiagRuleWithNodeList from "issara-sdk/apis/PublishedDiagRuleWithNodeList_apps_PRX";
import PublishedDiagRuleNodeMemberList from "issara-sdk/apis/PublishedDiagRuleNodeMemberList_apps_PRX";
import PublishedDiagRuleNodeMemberUpdateView from "issara-sdk/apis/PublishedDiagRuleNodeMemberUpdateView_apps_PRX";
import DiagRuleList from "issara-sdk/apis/DiagRuleList_apps_PRX";
import ProxyPatientDetail from "issara-sdk/apis/ProxyPatientDetail_apps_PRX";

/*================================ SERIALIZERS ==============================*/

import PublishedDiagRuleNodeSerializer from "issara-sdk/types/PublishedDiagRuleNodeSerializer_apps_PRX";
import DivisionSerializer from "issara-sdk/types/DivisionSerializer_core";
import BroadcastPostSerializer from "issara-sdk/types/BroadcastPostSerializer_apps_PRX";
import EmployeeSerializer from "issara-sdk/types/EmployeeSerializer_users";
import BroadcastPostActionLogSerializer from "issara-sdk/types/BroadcastPostActionLogSerializer_apps_PRX";
import PublishedDiagRuleWithNodeSerializer from "issara-sdk/types/PublishedDiagRuleWithNodeSerializer_apps_PRX";
import PublishedDiagRuleNodeMemberSerializer from "issara-sdk/types/PublishedDiagRuleNodeMemberSerializer_apps_PRX";
import DiagRuleSerializer from "issara-sdk/types/DiagRuleSerializer_apps_PRX";
import ProxyPatientSerializer from "issara-sdk/types/ProxyPatientSerializer_apps_PRX";

/*================================ TYPES ==============================*/

import {
  ACTIONS,
  SEQUENCE_NAME,
  State,
  Data,
  Actions,
  SeqState,
  ActionType,
  Params,
  Handler,
  Controller,
  // MeCastMemberGroupTab,
  // MeCastBroadcastListTab,
  // MeCastInsightsTab,
  BroadcastListResponse,
  DivisionListResponse,
  BroadcastPostParams,
  ActionBroadcastApprovalParams,
  ResolveBroadcastParamsResult,
  LoadingKey,
  BroadcastPostApiParams,
  ButtonLoadCheckKey,
  ErrorMessageKey,
  SuccessMessageKey,
  MemberGroupListParams,
  MemberGroupListResponse,
  MemberGroupNodeParams,
  MemberGroupNodeActionParams,
  PatientDetailMap,
} from "../MeCastType";

import {
  BroadcastDataFirebase,
  BROADCAST_CARD_NAME,
  COLLECTION,
  CreatedBroadcastData,
  CreatedBroadcastDataApi,
  MEHEALTH_BROADCAST_GS,
  PatientByLeafList,
  PatientByLeafResponse,
  PostCalculateCredit,
  PostCalculateCreditParams,
} from "../MeFormType";

/*================================ CONSTANTS ==============================*/

export const StateInitial: State = {
  MeCastSequence: null,
};

export const DataInitial: Data = {};

const Masters = [] as const;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

export const STYLES: Record<string, CSSProperties> = {
  primaryThemeBasicBtn: {
    backgroundColor: "white",
    border: "solid 1px var(--primary-theme-color)",
    borderRadius: "5px",
  },
  primaryThemeBtn: {
    backgroundColor: "var(--primary-theme-color)",
    color: "white",
    borderRadius: "5px",
  },
  primaryThemeLabel: {
    backgroundColor: "var(--primary-select-background)",
    color: "black",
    borderRadius: "5px",
  },
};

export const ICONS: Record<string, string> = {
  broadcast: "/static/images/meform/broadcast.png",
  broadcastGray: "/static/images/meform/broadcast_gray.png",
  cancelReview: "/static/images/meform/cancel_review.png",
};

export const DEFAULT_BROADCAST_POST_LIST_LIMIT = 10;
export const DEFAULT_MEMBER_GROUP_LIST_LIMIT = 10;

export const BROADCAST_HEADER = {
  CREATE: "Create New Broadcast",
  EDIT: "Edit Broadcast",
  VIEW: "View Broadcast"
};

const BATCH_SIZE = 5;
const BATCH_DELAY_MS = 100;

/*================================ HELPERS ==============================*/

const setLoading = (controller: Controller, key: LoadingKey, loading: boolean) => {
  controller.setState((prev) => ({
    loadingStatus: {
      ...prev.loadingStatus,
      [key]: loading,
    },
  }));
};

const setButtonLoadCheck = (
  controller: Controller,
  key: ButtonLoadCheckKey,
  status: BLC_STATUS_TYPE | null
) => {
  controller.setState((prev) => ({
    buttonLoadCheck: {
      ...prev.buttonLoadCheck,
      [key]: status,
    },
  }));
};

const setErrorMessage = (controller: Controller, key: ErrorMessageKey, error: any) => {
  controller.setState((prev) => ({
    errorMessage: {
      ...prev.errorMessage,
      [key]: error,
    },
  }));
};

const setSuccessMessage = (controller: Controller, key: SuccessMessageKey, message: any) => {
  controller.setState((prev) => ({
    successMessage: {
      ...prev.successMessage,
      [key]: message,
    },
  }));
};

/*================================ START SEQUENCE ==============================*/

export const Start: Handler<SeqState> = async (controller, params) => {
  controller.handleEvent({
    message: "GetMasterData",
    params: { masters: Masters },
  });

  const { broadcastParams, memberGroupParams } = params;

  // --- Init Data
  setLoading(controller, BROADCAST_CARD_NAME, true);

  const [
    [pdrNodeListResp],
    [diagRuleListResp],
    [broadcastListResp],
    [memberGroupListResp],
    [divisionListResp],
  ] = await Promise.all([
    getPublishedDiagRuleNodeList(controller, { division: controller.data.division }),
    getDiagRuleList(controller, {}),
    getBroadcastPostList(controller, {
      broadcastParams: broadcastParams as BroadcastPostParams,
    }),
    getMemberGroupList(controller, { filter: memberGroupParams as MemberGroupListParams }),
    getDivisionList(controller, {}),
  ]);

  setLoading(controller, BROADCAST_CARD_NAME, false);

  controller.setState(prev => ({
    MeCastSequence: {
      ...prev.MeCastSequence,
      sequenceIndex: "Action",
      commonData: {
        ...prev.MeCastSequence?.commonData,
        pdrNodeList: pdrNodeListResp?.items || [],
        diagFormList: diagRuleListResp || [],
      },
      memberGroupTab: {
        ...prev.MeCastSequence?.memberGroupTab,
        memberGroupList: {
          activePage: prev.MeCastSequence?.memberGroupTab?.memberGroupList?.activePage || 1,
          items: memberGroupListResp?.items || [],
          total: memberGroupListResp?.total || 0,
        },
      },
      broadcastListTab: {
        ...prev.MeCastSequence?.broadcastListTab,
        broadcastList: {
          activePage: prev.MeCastSequence?.broadcastListTab?.broadcastList?.activePage || 1,
          items: broadcastListResp?.items || [],
          total: broadcastListResp?.total || 0,
        },
      },
      broadcastApprovalTab: {
        ...prev.MeCastSequence?.broadcastApprovalTab,
        divisionList: {
          ...prev.MeCastSequence?.broadcastApprovalTab?.divisionList,
          items: divisionListResp?.items || [],
          total: divisionListResp?.total || 0,
        },
      },
    },
  }));
};

/*================================ ACTIONS ==============================*/

export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    // * --- Common
    // [ACTIONS.FETCH]: HandleFetch,
    /*
      Note
      - Approval Usage: Approve, Unapprove, Reject
      - Broadcast List Usage: Edit, Cancel, Cancel Review (Draft), Duplicate
    */
    [ACTIONS.ACTION_BROADCAST]: HandleActionBroadcastPost,

    // * --- Member Group Actions
    [ACTIONS.GET_MEMBER_GROUP_LIST]: HandleGetMemberGroupList,
    [ACTIONS.GET_MEMBER_NODE_LIST]: HandleGetMemberNodeList,
    [ACTIONS.ACTION_MEMBER_NODE]: HandleActionMemberNode,

    // * --- Broadcast List Actions
    [ACTIONS.GET_BROADCAST_POST_LIST]: HandleGetBroadcastPostList,
    [ACTIONS.SELECT_BROADCAST]: HandleSelectBroadcast,
    [ACTIONS.POST_BROADCAST]: HandleCreatePostBroadcast,
    [ACTIONS.UPDATE_BROADCAST]: HandleUpdatePostBroadcast,
    [ACTIONS.GET_POST_CALCULATE_CREDIT]: HandleGetPostCalculateCredit,
    [ACTIONS.GET_PATIENT_BY_LEAF]: HandleGetPatientByLeaf,
    [ACTIONS.GET_UPDATED_BY_USER_FIREBASE]: GetUpdatedByUserFirebase,

    // * --- Insights Actions

    // * --- Broadcast Approval Actions
    [ACTIONS.GET_BROADCAST_POST_DETAIL]: HandleGetBroadcastPostDetail,
    [ACTIONS.GET_DIVISION_LIST]: HandleGetDivisionList,
    [ACTIONS.GET_DIVISION_DETAIL]: HandleGetDivisionDetail,
    [ACTIONS.GET_BROADCAST_LOG]: HandleGetBroadcastLogList,
    [ACTIONS.DUPLICATE_BROADCAST]: HandleDuplicateBroadcastPost,

    // * --- Firebase Handlers
    [ACTIONS.ADD_CONTENT]: HandleAddContent,
    [ACTIONS.GET_CONTENT]: HandleGetContent,
    [ACTIONS.UPDATE_CONTENT]: HandleUpdateContent,
  };

  const action = params.action;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    if (["UPDATE_CONTENT", "GET_BROADCAST_POST_DETAIL"].includes(action)) {
      return handler(controller, params);
    } else {
      handler(controller, params);
    }
  } else {
    console.error(`The handler not found for action: ${action}`);
  }
};

/*================================ BROADCAST LIST HANDLERS ==============================*/

const HandleGetBroadcastPostList: Handler<Params<Actions["GET_BROADCAST_POST_LIST"]>> = async (
  controller,
  params
) => {
  const key = "broadcastList";

  const { broadcastParams } = params;

  const activePage = params.activePage ?? 1;
  const offset = (activePage - 1) * DEFAULT_BROADCAST_POST_LIST_LIMIT;

  setLoading(controller, key, true);

  const { params: resolvedParams, error } = await resolveBroadcastParams(controller, {
    broadcastParams,
  });

  if (error) {
    setLoading(controller, key, false);

    return;
  }

  const [resp, err] = await getBroadcastPostList(controller, {
    broadcastParams: resolvedParams,
    offset,
  });

  if (err || !resp?.items) {
    setLoading(controller, key, false);
    setErrorMessage(controller, key, err);

    return;
  }

  setLoading(controller, key, false);

  controller.setState((prev) => ({
    MeCastSequence: {
      ...prev.MeCastSequence,
      broadcastListTab: {
        ...prev.MeCastSequence?.broadcastListTab,
        broadcastList: {
          activePage,
          items: resp?.items || [],
          total: resp?.total || 0,
        },
        postCalculateCredit: undefined,
        broadcastDetail: undefined,
      },
      firebaseData: undefined,
    },
  }));
};

const HandleGetPostCalculateCredit: Handler<Params<Actions["GET_POST_CALCULATE_CREDIT"]>> = async (
  controller,
  params
) => {
  const { calculateParams } = params;

  const [resp, err] = await getPostCalculatedCredit(controller, { calculateParams });

  if (err || !resp) {
    setErrorMessage(controller, BROADCAST_CARD_NAME, err);

    return;
  }

  controller.setState((prev) => ({
    MeCastSequence: {
      ...prev.MeCastSequence,
      broadcastListTab: {
        ...prev.MeCastSequence?.broadcastListTab,
        postCalculateCredit: resp as PostCalculateCredit,
      },
    },
  }));
};

const HandleSelectBroadcast: Handler<Params<Actions["SELECT_BROADCAST"]>> = async (
  controller,
  params
) => {
  const { pdrNodeId } = params;

  setLoading(controller, BROADCAST_CARD_NAME, true);

  const [pdrResp, pdrErr] = await getPublishedDiagRuleNodeDetail(controller, { pdrNodeId });

  if (pdrErr || !pdrResp) {
    setLoading(controller, BROADCAST_CARD_NAME, false);
    setErrorMessage(controller, BROADCAST_CARD_NAME, pdrErr);

    return;
  }

  setLoading(controller, BROADCAST_CARD_NAME, false);

  controller.setState((prev) => ({
    MeCastSequence: {
      ...prev.MeCastSequence,
      broadcastListTab: {
        ...prev.MeCastSequence?.broadcastListTab,
        selectedPdrNode: pdrResp,
      },
    },
  }));
};

const HandleCreatePostBroadcast: Handler<Params<Actions["POST_BROADCAST"]>> = async (
  controller,
  params
) => {
  const { createdData } = params;
  const btnKey = `${BTN_ACTS.POST_BROADCAST}_${createdData.status}`;

  setButtonLoadCheck(controller, btnKey, BLC_STATUS.LOADING);

  const existingData = controller.getState().MeCastSequence?.firebaseData?.selectedBroadcast;

  const [fbId, fbErr] = await createBroadcastFirebase(controller, { createdData, existingData });

  if (fbErr || !fbId) {
    setButtonLoadCheck(controller, btnKey, BLC_STATUS.ERROR);
    setErrorMessage(controller, BROADCAST_CARD_NAME, fbErr);

    return;
  }

  const data: CreatedBroadcastDataApi = {
    ...createdData,
    firebase_id: fbId,
    division: controller.data.division ?? 0,
  };

  const [postResp, postErr] = await postBroadcast(controller, { data });

  if (postErr || !postResp) {
    setButtonLoadCheck(controller, btnKey, BLC_STATUS.ERROR);
    setErrorMessage(controller, BROADCAST_CARD_NAME, postErr);

    // fixed
    setButtonLoadCheck(controller, btnKey, null);
    return;
  }

  await GetUpdatedByUserFirebase(controller, { firebaseId: postResp?.firebase_id });

  setButtonLoadCheck(controller, btnKey, null);

  if (createdData.status === "DRAFTED") {
    setSuccessMessage(controller, BROADCAST_CARD_NAME, "The Broadcast's Post has drafted");
  }

  controller.setState(prev => ({
    MeCastSequence: {
      ...prev.MeCastSequence,
      broadcastListTab: {
        ...prev.MeCastSequence?.broadcastListTab,
        broadcastDetail: postResp,
      },
    },
  }));
};

const HandleUpdatePostBroadcast: Handler<Params<Actions["UPDATE_BROADCAST"]>> = async (controller, params) => {
  const { createdData, broadcastPostId } = params;
  const btnKey = `${BTN_ACTS.UPDATE_BROADCAST}_${createdData.status}`;

  setButtonLoadCheck(controller, btnKey, BLC_STATUS.LOADING);

  const existingData = controller.getState().MeCastSequence?.firebaseData?.selectedBroadcast;

  const [fbResp, fbErr] = await updateBroadcastFirebase(controller, { firebaseId: existingData?.id, data: existingData });

  if (fbErr || !fbResp?.id) {
    setButtonLoadCheck(controller, btnKey, BLC_STATUS.ERROR);
    setErrorMessage(controller, BROADCAST_CARD_NAME, fbErr);

    return;
  }

  const data: CreatedBroadcastDataApi = {
    ...createdData,
    firebase_id: fbResp.id,
    division: controller.data.division ?? 0,
  };

  const [updatedResp, updatedErr] = await updateBroadcast(controller, { broadcastPostId, data });

  if (updatedErr || !updatedResp) {
    setButtonLoadCheck(controller, btnKey, BLC_STATUS.ERROR);
    setErrorMessage(controller, BROADCAST_CARD_NAME, updatedErr);

    return;
  }

  await GetUpdatedByUserFirebase(controller, { firebaseId: fbResp.id });

  setButtonLoadCheck(controller, btnKey, null);

  if (createdData.status === "DRAFTED") {
    setSuccessMessage(controller, BROADCAST_CARD_NAME, "The Broadcast's Post has drafted");
  }

  controller.setState(prev => ({
    MeCastSequence: {
      ...prev.MeCastSequence,
      broadcastListTab: {
        ...prev.MeCastSequence?.broadcastListTab,
        broadcastDetail: updatedResp,
      },
    },
  }));

  // console.log("Yeti [HandleUpdatePost] Params: ", {params, existingData});
};

const HandleGetPatientByLeaf: Handler<Params<Actions["GET_PATIENT_BY_LEAF"]>> = async (controller, params) => {
  const [resp, err] = await getPatientByLeafNode(controller, {
    findPatientParams: {
      published_diag_rule_id: params.publishedDiagRuleId,
      leaf_node_id: params.nodeId
    },
  });

  if (err) return;

  controller.setState(prev => ({
    MeCastSequence: {
      ...prev.MeCastSequence,
      broadcastListTab: {
        ...prev.MeCastSequence?.broadcastListTab,
        patientByLeafList: {
          ...prev.MeCastSequence?.broadcastListTab?.patientByLeafList,
          [params.pdrNodeId]: resp,
        },
      },
    },
  }));
};

const GetUpdatedByUserFirebase: Handler<{ firebaseId: string }> = async (controller, params) => {
  const { firebaseId } = params;

  const fbBroadcast = await getBroadcastDetailFirebase(controller, { firebaseId });

  controller.setState(prev => ({
    MeCastSequence: {
      ...prev.MeCastSequence,
      broadcastListTab: {
        ...prev.MeCastSequence?.broadcastListTab,
        updatedByUserFirebase: fbBroadcast ? fbBroadcast?.updated_by : undefined,
      },
    },
  }));
};

/*================================ MEMBER GROUP HANDLERS ==============================*/

const HandleGetMemberGroupList: Handler<Params<Actions["GET_MEMBER_GROUP_LIST"]>> = async (
  controller,
  params
) => {
  const { filter } = params;

  const activePage = params.activePage ?? 1;
  const offset = (activePage - 1) * DEFAULT_MEMBER_GROUP_LIST_LIMIT;

  // setLoading(controller, BROADCAST_CARD_NAME, true);
  setButtonLoadCheck(controller, BTN_ACTS.GET_MEMBER_GROUP_LIST, BLC_STATUS.LOADING);

  const [resp, err] = await getMemberGroupList(controller, { filter, offset });

  if (err || !resp?.items) {
    // setLoading(controller, BROADCAST_CARD_NAME, false);
    setButtonLoadCheck(controller, BTN_ACTS.GET_MEMBER_GROUP_LIST, BLC_STATUS.ERROR);
    setErrorMessage(controller, BROADCAST_CARD_NAME, err);

    return;
  }

  // setLoading(controller, BROADCAST_CARD_NAME, false);
  setButtonLoadCheck(controller, BTN_ACTS.GET_MEMBER_GROUP_LIST, BLC_STATUS.SUCCESS);

  controller.setState(prev => ({
    MeCastSequence: {
      ...prev.MeCastSequence,
      memberGroupTab: {
        ...prev.MeCastSequence?.memberGroupTab,
        memberGroupList: {
          activePage,
          items: resp.items || [],
          total: resp.total || 0,
        },
        memberNodeList: [], // --
      },
    },
  }));
};

const HandleGetMemberNodeList: Handler<Params<Actions["GET_MEMBER_NODE_LIST"]>> = async (
  controller,
  params
) => {
  const { pdrNodeId, filter } = params;

  setButtonLoadCheck(controller, BTN_ACTS.GET_MEMBER_NODE_LIST, BLC_STATUS.LOADING);

  const [resp, err] = await getMemberGroupNodeList(controller, { pdrNodeId, filter });

  if (err || !resp) {
    setButtonLoadCheck(controller, BTN_ACTS.GET_MEMBER_NODE_LIST, BLC_STATUS.ERROR);
    setErrorMessage(controller, BROADCAST_CARD_NAME, err);

    return;
  }

  const patientIds = resp
    .map(member => member.patient)
    .filter((id): id is number => id !== undefined && id !== null);
  
  const existingCache = controller.getState().MeCastSequence?.memberGroupTab?.patientDetailMap || {};

  const patientDetailMap = await getPatientDetailsInBatches(controller, { patientIds, existingCache });

  setButtonLoadCheck(controller, BTN_ACTS.GET_MEMBER_NODE_LIST, BLC_STATUS.SUCCESS);

  controller.setState(prev => ({
    MeCastSequence: {
      ...prev.MeCastSequence,
      memberGroupTab: {
        ...prev.MeCastSequence?.memberGroupTab,
        memberNodeList: resp || [],
        patientDetailMap,
      },
    },
  }));
};

const HandleActionMemberNode: Handler<Params<Actions["ACTION_MEMBER_NODE"]>> = async (
  controller,
  params
) => {
  const { pdrNodeId, filter, data } = params;

  setButtonLoadCheck(controller, BTN_ACTS.ACTION_MEMBER_NODE, BLC_STATUS.LOADING);

  const [resp, err] = await actionMemberGroupNode(controller, { data });

  if (err || !resp) {
    setButtonLoadCheck(controller, BTN_ACTS.ACTION_MEMBER_NODE, BLC_STATUS.ERROR);
    setErrorMessage(controller, BROADCAST_CARD_NAME, err);

    return;
  }

  // -- Update List
  const [memberListResp, memberListErr] = await getMemberGroupNodeList(controller, { pdrNodeId, filter });

  if (memberListErr || !memberListResp) {
    setButtonLoadCheck(controller, BTN_ACTS.ACTION_MEMBER_NODE, BLC_STATUS.ERROR);
    setErrorMessage(controller, BROADCAST_CARD_NAME, memberListErr);

    return;
  }

  const patientIds = memberListResp
    .map(member => member.patient)
    .filter((id): id is number => id !== undefined && id !== null);

  const existingCache = controller.getState().MeCastSequence?.memberGroupTab?.patientDetailMap || {};

  const patientDetailMap = await getPatientDetailsInBatches(controller, {
    patientIds,
    existingCache,
  });

  // -- Update Member Group List
  const [memberGroupListResp, memberGroupListErr] = await getMemberGroupList(controller, { filter: {}, offset: 0 });

  if (memberGroupListErr || !memberGroupListResp) {
    setButtonLoadCheck(controller, BTN_ACTS.ACTION_MEMBER_NODE, BLC_STATUS.ERROR);
    setErrorMessage(controller, BROADCAST_CARD_NAME, memberGroupListErr);

    return;
  }

  setButtonLoadCheck(controller, BTN_ACTS.ACTION_MEMBER_NODE, BLC_STATUS.SUCCESS);

  controller.setState(prev => ({
    MeCastSequence: {
      ...prev.MeCastSequence,
      memberGroupTab: {
        ...prev.MeCastSequence?.memberGroupTab,
        memberNodeList: memberListResp || [],
        patientDetailMap,
        memberGroupList: {
          ...prev.MeCastSequence?.memberGroupTab?.memberGroupList,
          activePage: 1,
          items: memberGroupListResp.items || [],
          total: memberGroupListResp.total || 0,
        },
      },
    },
  }));
};

/*================================ APPROVAL HANDLERS ==============================*/

const HandleGetBroadcastPostDetail: Handler<Params<Actions["GET_BROADCAST_POST_DETAIL"]>> = async (
  controller,
  params
) => {
  const key = "broadcastDetail";

  const { broadcastPostId } = params;

  if (!broadcastPostId) return;

  setLoading(controller, key, true);

  const [resp, err] = await getBroadcastPostDetail(controller, { broadcastPostId });

  if (err || !resp) {
    setLoading(controller, key, false);
    setErrorMessage(controller, key, err);

    return;
  }

  setLoading(controller, key, false);

  controller.setState((prev) => ({
    MeCastSequence: {
      ...prev.MeCastSequence,
      broadcastApprovalTab: {
        ...prev.MeCastSequence?.broadcastApprovalTab,
        selectedBroadcast: resp,
      },
    },
  }));

  return resp;
};

export const HandleGetDivisionList: Handler<Params<"GET_DIVISION_LIST">> = async (
  controller,
  params
) => {
  const key = "divisionList";

  setLoading(controller, key, true);

  const [resp, err] = await getDivisionList(controller, params);

  if (err) {
    setLoading(controller, key, false);
    setErrorMessage(controller, key, err);

    return;
  }

  setLoading(controller, key, false);

  controller.setState((prev) => ({
    MeCastSequence: {
      ...prev.MeCastSequence,
      broadcastApprovalTab: {
        ...prev.MeCastSequence?.broadcastApprovalTab,
        divisionList: {
          ...prev.MeCastSequence?.broadcastApprovalTab?.divisionList,
          items: resp?.items || [],
          total: resp?.total || 0,
        },
      },
    },
  }));
};

export const HandleGetDivisionDetail: Handler<Params<"GET_DIVISION_DETAIL">> = async (
  controller,
  params
) => {
  const key = "divisionDetail";

  setLoading(controller, key, true);

  if (!params.id) {
    setLoading(controller, key, false);
    setErrorMessage(controller, key, "Division ID doesn't exist!");

    return;
  }

  const [resp, err] = await getDivisionDetail(controller, { divisionId: params.id });

  if (err) {
    setLoading(controller, key, false);
    setErrorMessage(controller, key, err);

    return;
  }

  setLoading(controller, key, false);

  controller.setState((prev) => ({
    MeCastSequence: {
      ...prev.MeCastSequence,
      broadcastApprovalTab: {
        ...prev.MeCastSequence?.broadcastApprovalTab,
        divisionDetail: resp as DivisionSerializer,
      },
    },
  }));
};

const HandleActionBroadcastPost: Handler<Params<Actions["ACTION_BROADCAST"]>> = async (
  controller,
  params
) => {
  // const state = controller.getState();
  const key = "actionBroadcast";

  const { broadcastPostId, actionParams, broadcastParams } = params;

  setLoading(controller, key, true);

  const [resp, err] = await actionPostBroadcast(controller, {
    postId: broadcastPostId,
    actionParams,
  });

  if (err || !resp) {
    setLoading(controller, key, false);
    setErrorMessage(controller, BROADCAST_CARD_NAME, err);

    return;
  }

  // --- Update Selected Broadcast
  const [selectedResp, selectedErr] = await getBroadcastPostDetail(controller, { broadcastPostId });

  if (selectedErr || !selectedResp) {
    setLoading(controller, key, false);
    setErrorMessage(controller, BROADCAST_CARD_NAME, selectedErr);

    return;
  }

  setLoading(controller, key, false);
  setSuccessMessage(
    controller,
    BROADCAST_CARD_NAME,
    `${actionParams.action.toLowerCase()} broadcast success!`
  );

  controller.setState((prev) => ({
    MeCastSequence: {
      ...prev.MeCastSequence,
      broadcastApprovalTab: {
        ...prev.MeCastSequence?.broadcastApprovalTab,
        selectedBroadcast: selectedResp,
      },
    },
  }));

  Action(controller, {
    sequence: "MeCast",
    action: ACTIONS.GET_BROADCAST_POST_LIST,
    broadcastParams: broadcastParams ?? {},
  });
};

const HandleDuplicateBroadcastPost: Handler<Params<Actions["DUPLICATE_BROADCAST"]>> = async (controller, params) => {
  const key = "actionBroadcast";

  const { broadcastPostId, broadcastParams, onSuccess } = params;

  setLoading(controller, key, true);

  const [resp, err] = await duplicateBroadcast(controller, { postId: broadcastPostId });

  if (err || !resp) {
    setLoading(controller, key, false);
    setErrorMessage(controller, BROADCAST_CARD_NAME, err);

    return;
  }

  setLoading(controller, key, false);

  setSuccessMessage(
    controller,
    BROADCAST_CARD_NAME,
    `duplicate broadcast success!`
  );

  Action(controller, {
    sequence: "MeCast",
    action: ACTIONS.GET_BROADCAST_POST_LIST,
    broadcastParams: broadcastParams ?? {},
  });

  onSuccess?.(resp);
};

const HandleGetBroadcastLogList: Handler<Params<Actions["GET_BROADCAST_LOG"]>> = async (
  controller,
  params
) => {
  const key = "broadcastLogList";

  const { broadcastPostId } = params;

  setLoading(controller, key, true);

  const [resp, err] = await getBroadcastLogList(controller, { broadcastPostId });

  if (err || !resp) {
    setLoading(controller, key, false);
    setErrorMessage(controller, BROADCAST_CARD_NAME, err);

    return;
  }

  setLoading(controller, key, false);
  
  controller.setState(prev => ({
    MeCastSequence: {
      ...prev.MeCastSequence,
      broadcastApprovalTab: {
        ...prev.MeCastSequence?.broadcastApprovalTab,
        selectedBroadcastLog: resp,
      },
    },
  }));
};

/*================================ CONTENT HANDLERS ==============================*/

const HandleAddContent: Handler = async (controller, params) => {
  const state = controller.getState();

  const collectionRef = collection(controller.db as Firestore, COLLECTION);
  const docRef = doc(collectionRef);

  const documentUid = docRef.id;

  const init: BroadcastDataFirebase = {
    id: documentUid,
    division_id: controller.data.division,
  };

  controller.setState({
    MeCastSequence: {
      ...state.MeCastSequence,
      firebaseData: {
        ...state.MeCastSequence?.firebaseData,
        selectedBroadcast: init,
      },
    },
  });
};

const HandleGetContent: Handler = async (controller, params) => {

  const selectedBroadcast = params.selectedBroadcast as BroadcastPostSerializer;

  const result = await getBroadcastDetailFirebase(controller, { firebaseId: selectedBroadcast?.firebase_id });

  // -- getCalculate Post data
  const calculateParams: PostCalculateCreditParams = {
    receiver_type: selectedBroadcast?.receiver_type,
    published_diag_rule_nodes: selectedBroadcast?.published_diag_rule_nodes,
  };

  const [postCalCreditResp, postCalCreditErr] = await getPostCalculatedCredit(controller, { calculateParams });

  controller.setState(prev => ({
    MeCastSequence: {
      ...prev.MeCastSequence,
      firebaseData: {
        ...prev.MeCastSequence?.firebaseData,
        selectedBroadcast: result,
      },
      broadcastListTab: {
        ...prev.MeCastSequence?.broadcastListTab,
        postCalculateCredit: postCalCreditResp as PostCalculateCredit,
      },
    },
  }));
};

const HandleUpdateContent: Handler<Params<Actions["UPDATE_CONTENT"]>> = async (
  controller,
  params
) => {
  // const state = controller.getState();

  let ret: any;

  if (!params.id) {
    console.warn("[HandleUpdateContent] ID doesn't exist!!");
    return undefined;
  }

  let broadcast = controller.getState().MeCastSequence?.firebaseData?.selectedBroadcast;

  if (!broadcast) {
    console.warn("[HandleUpdateContent] Broadcast Data doesn't exist!!");
    return undefined;
  }

  if ("content" in params) broadcast = { ...broadcast, content: params.content };

  if ("files" in params && "info" in params && params.files?.[0]) {
    ret = await uploadFileToBroadcastFolder(controller, {
      file: params.files[0],
      bucketName: MEHEALTH_BROADCAST_GS,
      broadcastId: params.id,
      idx: params.index || 0,
    });
  }

  const media = findMediaTagsAndText(params.content || "");
  const previewText = media?.previews_text?.slice(0, 175) || "";

  broadcast = {
    ...broadcast,
    shorthen_text: previewText,
    previews_text: media?.previews_text || "",
    previews_picture: media?.previews_pictures || [],
    previews_video: media?.previews_videos || [],
  };

  controller.setState(prev => ({
    MeCastSequence: {
      ...prev.MeCastSequence,
      firebaseData: {
        ...prev.MeCastSequence?.firebaseData,
        selectedBroadcast: broadcast,
        loading: false,
      },
    },
  }));

  return ret;
};

/*================================ API HANDLERS ==============================*/

// const resolvePatientByLeafList: Handler<
//   { nodes?: PublishedDiagRuleNodeSerializer[] },
//   Promise<PatientByLeafList | undefined>
// > = async (controller, params) => {
//   const { nodes } = params;

//   if (!nodes?.length) return undefined;

//   const results = await Promise.all(
//     nodes.map(async node => {
//       const [resp, err] = await getPatientByLeafNode(controller, {
//         findPatientParams: {
//           published_diag_rule_id: node.published_diag_rule,
//           leaf_node_id: node.id,
//         },
//       });

//       return {
//         nodeId: node.id,
//         value: err ? null : resp,
//       };
//     })
//   );

//   return results.reduce<PatientByLeafList>((acc, cur) => {
//     acc[cur.nodeId ?? 0] = cur.value;

//     return acc;
//   }, {});
// };

const resolveBroadcastParams: Handler<
  { broadcastParams?: BroadcastPostParams },
  Promise<ResolveBroadcastParamsResult>
> = async (controller, params) => {
  const { broadcastParams } = params;

  if (!broadcastParams?.group_name) {
    return { params: broadcastParams || {} };
  }

  const { group_name, ...apiParams } = broadcastParams;

  if (!group_name) {
    return { params: apiParams };
  }

  // All Member
  if (group_name === "All member") {
    return {
      params: {
        ...apiParams,
        receiver_type: "ALL_MEMBER",
        published_diag_rule_nodes: undefined,
      },
    };
  }

  // Specific Group
  const [nodesResp, nodesErr] = await getPublishedDiagRuleNodeList(controller, {
    division: apiParams.division,
    node_type: "end",
    node_name: group_name,
  });

  if (nodesErr || !nodesResp) {
    return { params: apiParams, error: nodesErr };
  }

  return {
    params: {
      ...apiParams,
      receiver_type: "SPECIFY",
      published_diag_rule_nodes: nodesResp.items.map((i) => i.id!),
    },
  };
};

const getDivisionList: Handler<any, Promise<[DivisionListResponse | null, any]>> = async (
  controller,
  params
) => {
  const [resp, err] = await DivisionList.list({
    apiToken: controller.apiToken,
    params,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return [null, err];

  return [resp, err];
};

const getDivisionDetail: Handler<
  { divisionId: number },
  Promise<[DivisionSerializer | null, any]>
> = async (controller, params) => {
  const [resp, err] = await DivisionDetail.retrieve({
    apiToken: controller.apiToken,
    pk: params.divisionId,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return [null, err];

  return [resp, err];
};

const getPublishedDiagRuleNodeList: Handler<
  any,
  Promise<[{ items: PublishedDiagRuleNodeSerializer[]; total: number } | null, any]>
> = async (controller, params) => {
  const [resp, err] = await PublishedDiagRuleNodeList.list({
    apiToken: controller.apiToken,
    params: {
      ...params,
      limit: 9999,
    },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return [null, err];

  return [resp, null];
};

const getDiagRuleList: Handler<
  any,
  Promise<[DiagRuleSerializer[] | null, any]>
> = async (controller, params) => {
  const [resp, err] = await DiagRuleList.list({
    apiToken: controller.apiToken,
    params: {
      active: true,
      division: controller.data.division,
      limit: 9999,
    },
    extra: { division: controller.data.division },
  });

  if (err || !resp.items) return [null, err];

  return [resp.items || [], null];
};

const getPublishedDiagRuleNodeDetail: Handler<
  { pdrNodeId: number },
  Promise<[PublishedDiagRuleNodeSerializer | null, any]>
> = async (controller, params) => {
  const [resp, err] = await PublishedDiagRuleNodeDetail.retrieve({
    apiToken: controller.apiToken,
    pk: params.pdrNodeId,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return [null, err];

  return [resp, null];
};

const getBroadcastPostList: Handler<
  { broadcastParams: BroadcastPostApiParams; offset?: number },
  Promise<[BroadcastListResponse | null, any]>
> = async (controller, params) => {
  const [resp, err] = await BroadcastPostListCreateView.list({
    apiToken: controller.apiToken,
    params: {
      ...params.broadcastParams,
      offset: params.offset,
      limit: DEFAULT_BROADCAST_POST_LIST_LIMIT,
    },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return [null, err];

  return [resp, null];
};

const getBroadcastPostDetail: Handler<
  { broadcastPostId: number },
  Promise<[BroadcastPostSerializer | null, any]>
> = async (controller, params) => {
  const [resp, err] = await BroadcastPostDetailView.retrieve({
    apiToken: controller.apiToken,
    pk: params.broadcastPostId,
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [null, err];

  return [resp, null];
};

const getPostCalculatedCredit: Handler<
  { calculateParams: PostCalculateCreditParams },
  Promise<[PostCalculateCredit | null, any]>
> = async (controller, params) => {
  if (!params.calculateParams?.published_diag_rule_nodes) {
    const errMsg = {
      published_diag_rule_nodes: ["Published Diag Rule Node ID doesn't exist!!"],
    };

    console.warn(`[getPostCalculatedCredit] ${errMsg.published_diag_rule_nodes}`);

    return [null, errMsg];
  }

  const [resp, err] = await BroadcastPostCalculateCreditView.get({
    apiToken: controller.apiToken,
    params: params.calculateParams,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return [null, err];

  return [resp, null];
};

const postBroadcast: Handler<
  { data?: CreatedBroadcastDataApi },
  Promise<[BroadcastPostSerializer | null, any]>
> = async (controller, params) => {
  const { data } = params;

  const [resp, err] = await BroadcastPostListCreateView.create({
    apiToken: controller.apiToken,
    data,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return [null, err];

  return [resp, null];
};

const updateBroadcast: Handler<
  { broadcastPostId: number; data?: CreatedBroadcastDataApi },
  Promise<[BroadcastPostSerializer | null, any]>
> = async (controller, params) => {
  const { broadcastPostId, data } = params;

  const [resp, err] = await BroadcastPostDetailView.update({
    apiToken: controller.apiToken,
    pk: broadcastPostId,
    data,
    extra: { division: controller.data.division },
  });

  if (err) return [null, err];

  return [resp, null];
};

const getUserDetail: Handler<{ user?: number | null }, Promise<EmployeeSerializer | null>> = async (
  controller,
  params
) => {
  if (!params.user) {
    console.warn("[getUserList] User doesn't exist!!");
    return null;
  }

  const [resp, err] = await UserEmployeeDetailAPIView.retrieve({
    apiToken: controller.apiToken,
    pk: params.user,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp;
};

const getPatientByLeafNode: Handler<
  {
    findPatientParams: {
      published_diag_rule_id: number;
      leaf_node_id?: number;
      leaf_node_label?: string;
      diag_rule_id?: number;
    };
  },
  Promise<[PatientByLeafResponse | null, any]>
> = async (controller, params) => {
  const { findPatientParams } = params;

  const [resp, err] = await FindPatientsByLeafNodeAPI.post({
    apiToken: controller.apiToken,
    data: findPatientParams,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return [null, err];

  return [resp, null];
};

const actionPostBroadcast: Handler<
  { actionParams: ActionBroadcastApprovalParams; postId: number },
  Promise<[BroadcastPostSerializer | null, any]>
> = async (controller, params) => {
  const [resp, err] = await BroadcastPostActionView.put({
    apiToken: controller.apiToken,
    pk: params.postId,
    data: params.actionParams,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return [null, err];

  return [resp, null];
};

const duplicateBroadcast: Handler<
  { postId: number },
  Promise<[BroadcastPostSerializer | null, any]>
> = async (controller, params) => {
  const [resp, err] = await BroadcastPostDuplicateView.post({
    apiToken: controller.apiToken,
    pk: params.postId,
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [null, err];

  return [resp, null];
};

const getBroadcastLogList: Handler<
  { broadcastPostId?: number },
  Promise<[BroadcastPostActionLogSerializer[] | null, any]>
> = async (controller, params) => {
  const [resp, err] = await BroadcastPostActionLogList.list({
    apiToken: controller.apiToken,
    params: {
      broadcast_post: params.broadcastPostId,
    },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return [null, err];

  return [resp.items || [], null];
};

const getBroadcastDetailFirebase: Handler<{ firebaseId: string }> = async (controller, params) => {
  const { firebaseId } = params;
  
  const db = controller.db as Firestore;
  const ref = doc(db, COLLECTION, firebaseId);
  const snap = await getDoc(ref);

  if (!snap.exists()) return null;

  return snap.data();
};

const getMemberGroupList: Handler<
  { filter: MemberGroupListParams; offset?: number },
  Promise<[ MemberGroupListResponse | null, any]>
> = async (controller, params) => {
  const [resp, err] = await PublishedDiagRuleWithNodeList.list({
    apiToken: controller.apiToken,
    params: {
      ...params.filter,
      offset: params.offset,
      limit: DEFAULT_MEMBER_GROUP_LIST_LIMIT,
    },
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [null, err];

  return [resp, null];
};

const getMemberGroupNodeList: Handler<
  { pdrNodeId: number; filter: MemberGroupNodeParams },
  Promise<[ PublishedDiagRuleNodeMemberSerializer[] | null, any ]>
> = async (controller, params) => {
  const [resp, err] = await PublishedDiagRuleNodeMemberList.list({
    apiToken: controller.apiToken,
    pk: params.pdrNodeId,
    params: {
      ...params.filter,
      limit: 9999,
    },
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [null, err];

  return [resp.items || [], null];
};

const actionMemberGroupNode: Handler<
  { data: MemberGroupNodeActionParams },
  Promise<[ PublishedDiagRuleNodeMemberSerializer[] | null, any ]>
> = async (controller, params) => {
  const [resp, err] = await PublishedDiagRuleNodeMemberUpdateView.put({
    apiToken: controller.apiToken,
    data: params.data,
    params: { limit: 9999 },
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [null, err];

  return [resp.items || [], null];
};

const getProxyPatientDetial: Handler<
  { patientId: number },
  Promise<ProxyPatientSerializer | null>
> = async (controller, params) => {
  const [resp, err] = await ProxyPatientDetail.retrieve({
    apiToken: controller.apiToken,
    pk: params.patientId,
    extra: { division: controller.data.division },
  });

  if (err || !resp) return null;

  return resp;
};

const getPatientDetailsInBatches: Handler<
  { patientIds: number[]; existingCache?: PatientDetailMap },
  Promise<PatientDetailMap>
> = async (controller, params) => {
  const { patientIds, existingCache = {} } = params;

  const uncachedIds = patientIds.filter(id => !existingCache[id]);

  if (uncachedIds.length === 0) {
    return existingCache;
  }

  const result: PatientDetailMap = { ...existingCache };
  const batches = chunkArray(uncachedIds, BATCH_SIZE);

  for (let i = 0; i < batches.length; i++) {
    const batch = batches[i];

    const batchResults = await Promise.allSettled(
      batch.map(patientId => getProxyPatientDetial(controller, { patientId }))
    );

    batchResults.forEach((res, index) => {
      const patientId = batch[index];
      if (res.status === "fulfilled" && res.value) {
        result[patientId] = res.value;
      }
    });

    if (i < batches.length - 1) {
      await delay(BATCH_DELAY_MS);
    }
  }

  return result;
};

/*================================ FIREBASE ==============================*/

const createBroadcastFirebase: Handler<
  { createdData: CreatedBroadcastData; existingData?: BroadcastDataFirebase },
  Promise<[string | null, any]>
> = async (controller, params) => {
  const { createdData, existingData } = params;

  try {
    const db = controller.db as Firestore;
    const broadcastId = existingData?.id || `broadcast_${Date.now()}`;

    const newData: BroadcastDataFirebase = {
      ...existingData,
      created_at: Timestamp.now(),
      updated_at: Timestamp.now(),
      status: createdData.status,
      is_active: true,
      version: 1,
      published_version: 1,
      author_id: controller.data.userProfile?.id,
      updated_by: {
        id: controller.data.userProfile?.id,
        name: controller.data.userProfile?.full_name,
      },
    };

    const docRef = doc(db, COLLECTION, broadcastId);

    await setDoc(docRef, newData);

    return [broadcastId, null];
  } catch (err) {
    console.error("[createBroadcast] Firebase create error: ", err);

    return [null, err];
  }
};

const updateBroadcastFirebase: Handler<{
  firebaseId?: string;
  data?: BroadcastDataFirebase;
}, Promise<[BroadcastDataFirebase | null, any]>> = async (controller, params) => {
  if (!params.firebaseId) {
    const errMsg = {
      updateBroadcastFirebase: "Firebase ID doesn't exist!!"
    };

    console.warn(`[updateBroadcastFirebase] ${errMsg.updateBroadcastFirebase}`);

    return [null, errMsg];
  };

  const db = controller.db as Firestore;
  const ref = doc(db, COLLECTION, params.firebaseId);

  await updateDoc(ref, {
    ...params.data,
    updated_at: Timestamp.now(),
    updated_by: {
      id: controller.data.userProfile?.id,
      name: controller.data.userProfile?.full_name,
    },
  });

  const updatedDocSnap = await getDoc(ref);

  if (!updatedDocSnap.exists()) return [null, "Error updating data!!"];

  return [updatedDocSnap.data(), null];
};

/*================================ UTILS ==============================*/

const findMediaTagsAndText = (content: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, "text/html");

  const images = Array.from(doc.querySelectorAll("img"));
  const videos = Array.from(doc.querySelectorAll("iframe"));
  const texts = doc.body.textContent || doc.body.innerText || "";

  const parsedContent = {
    previews_text: texts,
    previews_pictures: images.map((img) => img.src),
    previews_videos: videos.map((video) => video.src),
  };

  return parsedContent;
};

const uploadFileToBroadcastFolder = async (
  controller: any,
  params: {
    file: File;
    bucketName: `gs://${string}`;
    broadcastId: string;
    idx: number;
  }
): Promise<{
  url: string;
  name: string;
  size: number;
}> => {
  const fileName = `${params.idx}_${Date.now()}_${params.file.name}`;
  const objectPath = `images/${params.broadcastId}/${fileName}`;

  const storageRef = ref(controller.storage, `${params.bucketName}/${objectPath}`);

  const metadata = params.file.type ? { contentType: params.file.type } : undefined;

  const uploadTask = uploadBytesResumable(storageRef, params.file, metadata);

  return new Promise((resolve, reject) => {
    uploadTask.on(
      "state_changed",
      () => {},
      (err) => reject(err),
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);

        resolve({
          url,
          name: params.file.name,
          size: params.file.size,
        });
      }
    );
  });
};

const chunkArray = <T>(array: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
